/**
 * 現像レシピ（シート）
 *
 * Recipe = { id, name, filmId, ei, developerId, dilution?, tempC, nTimeSec,
 *            agitation: 'standard'|'gentle'|'aggressive'|'stand', notes, createdAt, updatedAt }
 * localStorage `zn.recipes.v1`。
 *
 * 「このシーンに適用」：現像液・温度・N 時間・攪拌の型をシーンに反映する。
 * フィルムと EI は撮影済みの記録なので変えない（EI が違うときは知らせる）。
 * 個人補正はフィルム × 現像液ごとの記憶をそのまま使う。
 */

import { openSheet, escapeHtml, toast, confirmDialog, icon, buzz } from './ui.js';
import { FILMS, getFilmById } from './data/films.js';
import { DEVELOPERS, getDeveloperById } from './data/developers.js';
import { getCurrentShoot, updateCurrentShoot } from './scene.js';
import { formatSeconds } from './data/dev-times.js';

const STORAGE_KEY = 'zn.recipes.v1';
// 標準・強め＝30 秒ごと、穏やか＝60 秒ごと、スタンド＝最初の 30 秒と半分で 1 回（developer.js の AGITATION）
const AGITATION = [['standard', '標準'], ['gentle', '穏やか'], ['aggressive', '強め'], ['stand', 'スタンド']];

function loadAll() {
  try {
    const arr = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function saveAll(arr) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
}

export function openRecipes() {
  const body = document.createElement('div');
  const sheet = openSheet({ en: 'recipes', title: '現像レシピ', body, full: true });
  const paint = () => {
    const recipes = loadAll().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const shoot = getCurrentShoot();
    body.innerHTML = `
      ${recipes.length === 0 ? `<div class="empty" style="margin-top:20px"><div class="empty__t">レシピがありません</div><p class="empty__d">下のボタンから、いつもの現像の条件を登録できます。</p></div>` : ''}
      ${recipes.map((r, i) => {
        const film = getFilmById(r.filmId);
        const dev = getDeveloperById(r.developerId);
        const ag = AGITATION.find((a) => a[0] === r.agitation)?.[1] || '標準';
        const on = shoot?.recipeId === r.id;
        return `
          <div class="rec" style="animation-delay:${i * 60}ms">
            <div style="display:flex;align-items:flex-start;gap:6px">
              <div style="flex:1;min-width:0"><div style="font:500 16px/1.35 var(--font-jp-sans)">${escapeHtml(r.name || 'Untitled')}</div>
              <div class="en sub" style="font-size:12px;line-height:1.55;margin-top:4px">${escapeHtml(`${film?.name || r.filmId} · EI ${r.ei} · ${ag}攪拌 · ${dev ? `${dev.name} ${dev.dilution}` : r.developerId}`)}</div></div>
              <button class="bar__btn" type="button" data-edit="${escapeHtml(r.id)}" aria-label="編集" style="width:40px;height:40px">${icon('edit', 18)}</button>
              <button class="bar__btn" type="button" data-del="${escapeHtml(r.id)}" aria-label="削除" style="width:40px;height:40px">${icon('trash', 18)}</button>
            </div>
            <div class="rec__acts">
              <span style="font:300 26px/1 var(--font-en)">${escapeHtml(formatSeconds(r.nTimeSec))}</span>
              <span class="en sub" style="font-size:13px">${escapeHtml(Number(r.tempC).toFixed(1))}°C</span>
              ${shoot ? `<button class="rec__apply ${on ? 'is-on' : ''}" type="button" data-apply="${escapeHtml(r.id)}">${on ? '適用中' : 'このシーンに適用'}</button>` : ''}
            </div>
          </div>`;
      }).join('')}
      <div style="padding:16px">
        <button class="btn-line btn-line--block" type="button" data-new style="height:52px;font-size:15px">${icon('plus', 16)}新規レシピを追加</button>
        <div class="sub" style="font-size:11px;line-height:1.6;margin-top:10px">適用すると、現像液・温度・時間（N）・攪拌の型をこのシーンに反映します（撮影時の EI は変えません）。個人補正はフィルム × 現像液ごとの値を使います。</div>
      </div>`;
    body.querySelector('[data-new]').addEventListener('click', () => openEdit(null));
    body.querySelectorAll('[data-edit]').forEach((b) => b.addEventListener('click', () => openEdit(b.dataset.edit)));
    body.querySelectorAll('[data-del]').forEach((b) => b.addEventListener('click', async () => {
      const ok = await confirmDialog({ title: 'このレシピを削除しますか？', ok: '削除' });
      if (!ok) return;
      saveAll(loadAll().filter((x) => x.id !== b.dataset.del));
      toast('レシピを削除しました');
      paint();
    }));
    body.querySelectorAll('[data-apply]').forEach((b) => b.addEventListener('click', () => {
      applyRecipe(b.dataset.apply);
      sheet.close();
    }));
  };
  paint();
}

function openEdit(id) {
  const recipe = id ? loadAll().find((r) => r.id === id) : null;
  const shoot = getCurrentShoot();
  const r = recipe || {
    id: '', name: '', filmId: shoot?.filmId || 'tri-x-400', ei: shoot?.ei || 400,
    developerId: shoot?.defaultDeveloperId || 'd76-1+1', tempC: 20, nTimeSec: 585, agitation: 'standard', notes: '',
  };
  let agitation = r.agitation || 'standard';
  const body = document.createElement('div');
  body.innerHTML = `
    <div style="padding:4px var(--gutter) 0">
      <label class="field"><span class="field__label">名前</span><input class="input" id="re-name" value="${escapeHtml(r.name)}" placeholder="例：Tri-X 320 · D-76 1+1" /></label>
      <label class="field"><span class="field__label">フィルム</span><select class="select" id="re-film">${FILMS.map((f) => `<option value="${escapeHtml(f.id)}" ${f.id === r.filmId ? 'selected' : ''}>${escapeHtml(`${f.brand} ${f.name}`)}</option>`).join('')}</select></label>
      <label class="field"><span class="field__label">EI（6–6400）</span><input class="input en" id="re-ei" inputmode="numeric" value="${escapeHtml(String(r.ei))}" /></label>
      <label class="field"><span class="field__label">現像液</span><select class="select" id="re-dev">${DEVELOPERS.map((d) => `<option value="${escapeHtml(d.id)}" ${d.id === r.developerId ? 'selected' : ''}>${escapeHtml(`${d.name} ${d.dilution}`)}</option>`).join('')}</select></label>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
        <label class="field"><span class="field__label">温度 °C（10–30）</span><input class="input en" id="re-temp" inputmode="decimal" value="${escapeHtml(String(r.tempC))}" /></label>
        <label class="field"><span class="field__label">時間 N（0:30–60:00）</span><input class="input en" id="re-time" value="${escapeHtml(formatSeconds(r.nTimeSec))}" placeholder="9:45" /></label>
      </div>
      <div class="field"><span class="field__label">攪拌</span><div class="seg" id="re-ag">${AGITATION.map(([v, t]) => `<button type="button" class="seg__opt ${agitation === v ? 'is-on' : ''}" data-ag="${v}">${t}</button>`).join('')}</div></div>
      <label class="field"><span class="field__label">メモ</span><textarea class="textarea" id="re-notes" placeholder="例：最初の 30 秒は連続攪拌">${escapeHtml(r.notes || '')}</textarea></label>
    </div>`;
  const foot = document.createElement('div');
  foot.innerHTML = '<button class="btn-primary" type="button" id="re-save" style="height:58px">保存して一覧に戻る</button>';
  openSheet({ en: id ? 'edit recipe' : 'new recipe', title: id ? 'レシピを編集' : '新規レシピ', body, foot, full: true });
  const $ = (s) => body.querySelector(s);
  $('#re-film').addEventListener('change', (e) => { $('#re-ei').value = String(getFilmById(e.target.value)?.iso || $('#re-ei').value); });
  body.querySelectorAll('[data-ag]').forEach((b) => b.addEventListener('click', () => {
    agitation = b.dataset.ag;
    body.querySelectorAll('[data-ag]').forEach((x) => x.classList.toggle('is-on', x === b));
  }));
  foot.querySelector('#re-save').addEventListener('click', () => {
    const ei = parseInt($('#re-ei').value, 10);
    const temp = parseFloat($('#re-temp').value);
    const sec = parseTime($('#re-time').value);
    const filmId = $('#re-film').value;
    const devId = $('#re-dev').value;
    const dev = getDeveloperById(devId);
    const next = {
      id: id || `rec_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      name: $('#re-name').value.trim() || `${getFilmById(filmId)?.name || filmId} · ${dev ? `${dev.name} ${dev.dilution}` : devId}`,
      filmId,
      ei: Number.isFinite(ei) ? Math.max(6, Math.min(6400, ei)) : 400,
      developerId: devId,
      tempC: Number.isFinite(temp) ? Math.max(10, Math.min(30, Math.round(temp * 10) / 10)) : 20,
      nTimeSec: Math.max(30, Math.min(3600, sec ?? 585)),
      agitation,
      notes: $('#re-notes').value.trim(),
      createdAt: recipe?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };
    saveAll([...loadAll().filter((x) => x.id !== next.id), next]);
    toast('レシピを保存しました');
    openRecipes();
  });
}

/** "9:45" または秒数 → 秒 */
function parseTime(v) {
  const s = String(v || '').trim();
  const m = s.match(/^(\d{1,2}):(\d{1,2})$/);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

function applyRecipe(id) {
  const recipe = loadAll().find((r) => r.id === id);
  const shoot = getCurrentShoot();
  if (!recipe || !shoot) { toast('シーンがありません'); return; }
  // 反映するのは現像の条件（現像液・温度・N 時間）だけ。
  // フィルムと EI は撮影済みの記録なので書き換えない（書き換えると露出の記録が計算し直されてしまう）
  updateCurrentShoot({
    defaultDeveloperId: recipe.developerId,
    devTempC: recipe.tempC,
    devNSec: recipe.nTimeSec,
    devAgitation: recipe.agitation || 'standard',
    recipeId: recipe.id,
  });
  buzz(8);
  const dev = getDeveloperById(recipe.developerId);
  const eiNote = Number(recipe.ei) !== Number(shoot.ei) ? `（レシピは EI ${recipe.ei} 用。このシーンは EI ${shoot.ei} で撮影）` : '';
  toast(`適用：${dev ? `${dev.name} ${dev.dilution}` : recipe.developerId} · ${Number(recipe.tempC).toFixed(1)}°C · N=${formatSeconds(recipe.nTimeSec)}${eiNote}`, eiNote ? 4200 : 2600);
  import('./developer.js').then((m) => m.refreshDevelopment());
}
